'use client';

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle, AlertCircle } from 'lucide-react';
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase";

const COUNTRY_PHONE_RULES = [
  { country: 'India', code: '+91', digits: 10 },
  { country: 'United States', code: '+1', digits: 10 },
  { country: 'United Kingdom', code: '+44', digits: 10 },
  { country: 'UAE', code: '+971', digits: 9 },
  { country: 'Australia', code: '+61', digits: 9 },
  { country: 'Germany', code: '+49', digits: 10 },
  { country: 'France', code: '+33', digits: 9 },
  { country: 'Singapore', code: '+65', digits: 8 },
  { country: 'Japan', code: '+81', digits: 10 },
  { country: 'Canada', code: '+1', digits: 10 },
];

const vacancies = [
  {
    title: 'Junior Architect',
    location: 'Greater kailash, New Delhi',
    type: 'Full-time',
    description:
      'We are looking for a motivated architect with strong design sensibilities, software proficiency, and an eagerness to learn.',
  },
  {
    title: 'Architectural Intern',
    location: 'Greater kailash, New Delhi',
    type: '4-6 Months',
    description:
      'The studio is seeking an architectural intern with developing design sensibilities, foundational software skills in Rhino, Revit and D5 with a strong willingness to learn.',
  },
];

const INPUT_BASE =
  'w-full border border-gray-300 rounded-lg px-4 py-2 leading-normal focus:outline-none focus:border-violet-500';

const CareerPage = () => {
  const [startTime] = useState(Date.now());
  const [honeypot, setHoneypot] = useState('');
  const [loading, setLoading] = useState(false);
  const [popup, setPopup] = useState({ show: false, message: '', type: 'success' });

  const [country, setCountry] = useState(COUNTRY_PHONE_RULES[0]);
  const [phone, setPhone] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    position: '',
    message: '',
    resume: null,
  });

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= country.digits) setPhone(value);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      position: '',
      message: '',
      resume: null,
    });
    setPhone('');
  };

  const showPopup = (message, type = 'success') => {
    setPopup({ show: true, message, type });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (honeypot !== '') return;
    if (Date.now() - startTime < 4000) return;

    if (phone.length !== country.digits) {
      showPopup('Invalid phone number format for the selected country.', 'error');
      return;
    }

    try {
      setLoading(true);

      let resumeUrl = null;

      if (formData.resume) {
        if (formData.resume.size > 10 * 1024 * 1024) {
          throw new Error('File size exceeds the 10MB limit.');
        }
        const fileName = `${Date.now()}-${formData.resume.name}`;
        const storageRef = ref(storage, `resumes/${fileName}`);
        const snapshot = await uploadBytes(storageRef, formData.resume);
        resumeUrl = await getDownloadURL(snapshot.ref);
      }

      await addDoc(collection(db, "jobApplications"), {
        name: formData.name,
        email: formData.email,
        countryCode: country.code,
        phone: phone,
        position: formData.position,
        message: formData.message,
        resumeUrl: resumeUrl,
        status: "unread",
        createdAt: serverTimestamp()
      });

      showPopup('Application submitted successfully! We will review your profile and get back to you soon.', 'success');
      resetForm();
      const resumeInput = document.getElementById('resume-upload');
      if (resumeInput) resumeInput.value = null;

    } catch (err) {
      console.error(err);
      showPopup('Something went wrong submitting your application. ' + (err.message || 'Please try again later.'), 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="careers" className="bg-gray-50 text-gray-800 relative">

      {/* POPUP MODAL */}
      <AnimatePresence>
        {popup.show && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 10 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
            >
              <div className="relative p-8 flex flex-col items-center text-center">
                <button
                  onClick={() => setPopup({ ...popup, show: false })}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition"
                >
                  <X size={20} />
                </button>

                {popup.type === 'success' ? (
                  <div className="w-14 h-14 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-5">
                    <CheckCircle size={32} />
                  </div>
                ) : (
                  <div className="w-14 h-14 bg-red-100 text-red-600 rounded-full flex items-center justify-center mb-5">
                    <AlertCircle size={32} />
                  </div>
                )}

                <h3 className="text-2xl font-medium mb-2">
                  {popup.type === 'success' ? 'Success!' : 'Oops!'}
                </h3>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  {popup.message}
                </p>

                <button
                  onClick={() => setPopup({ ...popup, show: false })}
                  className={`w-full py-3 rounded-xl text-white font-medium transition ${popup.type === 'success'
                    ? 'bg-violet-600 hover:bg-violet-700'
                    : 'bg-red-500 hover:bg-red-600'
                    }`}
                >
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <div className="py-24 px-8 md:px-20">
        <motion.h2
          className="text-3xl md:text-5xl font-light mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          Join Our Team
        </motion.h2>

        <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-x-20 gap-y-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
            {vacancies.map((job, i) => (
              <motion.div
                key={job.title}
                className="bg-white rounded-2xl p-8 shadow-md h-fit"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
              >
                <h3 className="text-2xl font-medium mb-1">{job.title}</h3>
                <p className="text-sm text-violet-600 mb-3">
                  {job.location} · {job.type}
                </p>
                <p className="text-gray-600">{job.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="bg-white rounded-2xl shadow-md p-10 h-fit">
            <motion.h3 className="text-3xl font-light mb-8">
              Apply Now
            </motion.h3>

            <form onSubmit={handleSubmit} className="space-y-6">
              <input
                type="text"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                className="hidden"
              />

              <input required name="name" value={formData.name} onChange={handleChange} placeholder="Full Name" className={INPUT_BASE} />
              <input required type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Email" className={INPUT_BASE} />

              <div className="flex gap-3">
                <select
                  value={country.code}
                  onChange={(e) =>
                    setCountry(COUNTRY_PHONE_RULES.find((c) => c.code === e.target.value))
                  }
                  className={`${INPUT_BASE} w-[140px]`}
                >
                  {COUNTRY_PHONE_RULES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.country} ({c.code})
                    </option>
                  ))}
                </select>

                <input
                  required
                  type="tel"
                  value={phone}
                  onChange={handlePhoneChange}
                  placeholder={`Phone (${country.digits} digits)`}
                  className={INPUT_BASE}
                />
              </div>

              <select
                required
                name="position"
                value={formData.position}
                onChange={handleChange}
                className={`${INPUT_BASE} h-[42px]`}
              >
                <option value="">Select Position</option>
                {vacancies.map((job) => (
                  <option key={job.title} value={job.title}>
                    {job.title}
                  </option>
                ))}
              </select>

              <textarea
                name="message"
                rows="4"
                value={formData.message}
                onChange={handleChange}
                placeholder="Why do you want to join us?"
                className={INPUT_BASE}
              />

              <div className="flex flex-col gap-1">
                <label className="text-sm text-gray-600 font-medium">Upload Portfolio / CV / Resume (Optional)</label>
                <input
                  id="resume-upload"
                  type="file"
                  accept=".pdf"
                  name="resume"
                  onChange={handleChange}
                  className={INPUT_BASE}
                />
                <p className="text-xs text-gray-500">Must be a PDF file under 10MB.</p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-violet-600 text-white py-3 rounded-lg hover:bg-violet-700 transition disabled:opacity-50"
              >
                {loading ? 'Submitting...' : 'Submit Application'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CareerPage;
